import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { fetchSession } from "../../api/client.js";
import { useGo } from "../../app/use-go.js";

import { accountUrls } from "./urls.js";

/**
 * ログインしていなければ、戻り先を付けてログインへ送る。
 *
 * <b>開いたときに読むものが無い画面</b>（パスワードの変更・二段階認証の解除）のためのもの。
 * 退会は、確認ダイアログの文に入れるアドレスを受け取るために使う。
 * 読むものがある画面は <c>useApiData</c> が 401 で同じことをする。
 * <b>守っているのは API 側</b>（送れば 401）で、ここは入力させてから断らないためだけにある。
 *
 * ログインしているアドレスを返す（確認ダイアログの文に入れる）。聞き終わるまでと、聞けなかったときは undefined。
 */
export const useSignedInOnly = (): string | undefined => {
    const go = useGo();
    const { pathname, search } = useLocation();
    const [email, setEmail] = useState<string | undefined>();

    useEffect(() => {
        let current = true;

        void fetchSession()
            .then((session) => {
                if (current && !session.signedIn) {
                    go(`${accountUrls.login}?returnUrl=${encodeURIComponent(pathname + search)}`);
                } else if (current) {
                    setEmail(session.email ?? undefined);
                }
            })
            // 状態を聞けなければ画面はそのまま出す。送ったときに API が断る
            .catch(() => {
                // 何もしない
            });

        return () => {
            current = false;
        };
    }, [go, pathname, search]);

    return email;
};

/** 確認ダイアログの文での自分のアカウントの呼び方。アドレスが分からなければ「このアカウント」。 */
export const accountOf = (email: string | undefined): string =>
    email === undefined ? "このアカウント" : `アカウント「${email}」`;
